import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
} from "react";
import {
  BotMessageSquare,
  X,
  Loader,
  MicIcon,
  MicOff,
  Settings,
} from "lucide-react";
import { Button, Form, Card, Container, Row, Col } from "react-bootstrap";
import assistantApi from "../../../services/assistantApiService";
import speechApi from "../../../services/speechApiService";
import AudioDeviceSelector from "../../../components/AudioDeviceSelector";
import { useAuth } from "../../../contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import { cos } from "@tensorflow/tfjs";

const STORAGE_KEY = "defaultMicrophoneId";
const DISPLAY_MESSAGE_LIMIT = 6; // Define a limit for messages kept in state

const Assistant = React.forwardRef(
  (
    { handleDateClick, reloadContent, onMicrophoneReleased, sharedMediaStream },
    ref
  ) => {
    // --- State ---
    const [messages, setMessages] = useState([]);
    const messageHistoryLimit = 4; // Limit for API context (already exists)
    const [inputMessage, setInputMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false); // For API calls
    const [isSpeechLoading, setIsSpeechLoading] = useState(false); // For starting speech recognition
    const [error, setError] = useState(null); // For API errors
    const [speechError, setSpeechError] = useState(null); // For Speech API errors
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState(""); // Final transcript from speech API
    const [partialTranscript, setPartialTranscript] = useState(""); // Partial transcript
    const [audioDeviceId, setAudioDeviceId] = useState(() =>
      localStorage.getItem(STORAGE_KEY)
    );
    const [showDeviceSelector, setShowDeviceSelector] = useState(false);
    const [processingAssistantResponse, setProcessingAssistantResponse] =
      useState(false);

    // --- Refs ---
    const messagesEndRef = useRef(null);
    const websocketRef = useRef(null);
    const microphoneStreamRef = useRef(null); // Holds the stream being USED (shared or acquired)
    const isStreamSharedRef = useRef(false); // Tracks if the current stream is the shared one
    const isMountedRef = useRef(true); // Track mount status

    // --- Context ---
    const { user } = useAuth();

    // --- Imperative Handle: Expose methods to parent (AppFooter via AssistantWrapper) ---
    useImperativeHandle(ref, () => ({
      startListening: (stream = null) => {
        _startListening(stream);
      },
      stopListening: _stopListening,
      isListening: isListening,
    }));

    // --- Lifecycle and Cleanup ---
    useEffect(() => {
      isMountedRef.current = true;
      console.log("Assistant Mounted");
      return () => {
        isMountedRef.current = false;
        console.log("Assistant Unmounted - cleaning up speech resources");
        _stopListening(true);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty array: Run only on mount/unmount

    useEffect(() => {
      scrollToBottom();
    }, [messages]);

    // Update input field when final transcript arrives
    useEffect(() => {
      if (transcript) {
        console.log("Assistant: Processing final transcript:", transcript);

        // Set the transcript in the input field
        setInputMessage(transcript);

        // Also add it to the messages list immediately for display
        const userMessage = { role: "user", content: transcript };
        setMessages((prev) =>
          [...prev, userMessage].slice(-DISPLAY_MESSAGE_LIMIT)
        );

        // Clear transcript state once used
        setTranscript("");
      }
    }, [transcript]);

    // --- Microphone Management ---
    const releaseMicrophoneStream = useCallback(() => {
      if (!microphoneStreamRef.current) {
        return;
      }
      const wasStreamShared = isStreamSharedRef.current;
      try {
        if (!wasStreamShared) {
          microphoneStreamRef.current.getTracks().forEach((track) => {
            if (track.readyState === "live") {
              track.stop();
              console.log(
                `Assistant: Track ${track.kind} (${track.label}) stopped.`
              );
            }
          });
        }
      } catch (error) {
        console.error("Assistant: Error stopping microphone tracks:", error);
      } finally {
        microphoneStreamRef.current = null;
        isStreamSharedRef.current = false;
        if (onMicrophoneReleased && isMountedRef.current) {
          onMicrophoneReleased();
        } else {
          console.log(
            "Assistant: Not notifying parent (unmounted or no callback)."
          );
        }
      }
    }, [onMicrophoneReleased]);

    // --- Handle received assistant response from speech recognition ---
    const handleAssistantResponse = useCallback(
      async (speechText, assistantData) => {
        if (!isMountedRef.current) return;

        console.log("Assistant: Processing speech + assistant response", {
          speechText,
          assistantData,
        });
        // Add detailed logging
        if (typeof assistantData === "string") {
          console.log("Assistant: assistantData is a string:", assistantData);
        } else if (assistantData && typeof assistantData === "object") {
          console.log(
            "Assistant: assistantData is an object with keys:",
            Object.keys(assistantData)
          );
          if (assistantData.response) {
            console.log(
              "Assistant: assistantData.response:",
              assistantData.response.substring(0, 50) + "..."
            );
          }
        } else {
          console.log("Assistant: assistantData is:", typeof assistantData);
        }

        setProcessingAssistantResponse(true);

        try {
          // First add the user message (the recognized speech text)
          if (speechText) {
            console.log(
              "Assistant: Adding user speech message to display:",
              speechText
            );
            const userMessage = { role: "user", content: speechText };
            setMessages((prev) =>
              [...prev, userMessage].slice(-DISPLAY_MESSAGE_LIMIT)
            );
          }

          // Then add the assistant's response
          if (assistantData && assistantData.response) {
            const assistantContent = assistantData.response;
            console.log(
              "Assistant: Adding assistant response to display:",
              assistantContent.substring(0, 50) + "..."
            );

            const assistantMessage = {
              role: "assistant",
              content: assistantContent,
            };
            setMessages((prev) =>
              [...prev, assistantMessage].slice(-DISPLAY_MESSAGE_LIMIT)
            );

            // Check for journal entry and handle date if needed
            if (assistantContent.includes("Journal Entry for")) {
              console.log("Assistant: Journal entry detected in response");
              const dateMatch = assistantContent.match(
                /Journal Entry for (\d{4}-\d{2}-\d{2})/
              );
              if (dateMatch && dateMatch[1]) {
                console.log(
                  "Assistant: Date found in journal entry:",
                  dateMatch[1]
                );
                const date = new Date(dateMatch[1]);
                handleDateClick(date);
                await reloadContent(date);
              }
            }
          } else {
            console.warn("Assistant: No valid assistant response to display");
          }
        } catch (error) {
          console.error("Assistant: Error handling assistant response:", error);
          setError("Failed to process assistant response");
        } finally {
          setProcessingAssistantResponse(false);
        }
      },
      [handleDateClick, reloadContent]
    );

    // --- Speech Recognition Logic ---
    const _startListening = useCallback(
      async (stream = null) => {
        if (isListening || isSpeechLoading) {
          console.log(
            "Assistant: Already listening or starting, ignoring request."
          );
          return;
        }
        if (websocketRef.current) {
          console.warn(
            "Assistant: WebSocket ref exists but not listening? Forcing cleanup."
          );
          websocketRef.current.forceStop?.(); // Use optional chaining for safety
          websocketRef.current = null;
        }

        releaseMicrophoneStream();

        console.log("Assistant: Starting speech recognition process...");
        setIsSpeechLoading(true);
        setSpeechError(null);
        setInputMessage("");
        setPartialTranscript("");

        let streamToUse = null;
        let deviceIdToUse = audioDeviceId;

        try {
          if (stream) {
            streamToUse = stream;
            isStreamSharedRef.current = true;
            deviceIdToUse = null;
          } else {
            isStreamSharedRef.current = false;
            if (audioDeviceId) {
              const devices = await navigator.mediaDevices.enumerateDevices();
              const audioInputDevices = devices.filter(
                (d) => d.kind === "audioinput"
              );
              if (
                !audioInputDevices.some((d) => d.deviceId === audioDeviceId)
              ) {
                console.warn(
                  `Assistant: Stored deviceId "${audioDeviceId}" is invalid. Clearing.`
                );
                setAudioDeviceId(null);
                localStorage.removeItem(STORAGE_KEY);
                deviceIdToUse = null;
              } else {
                deviceIdToUse = audioDeviceId;
              }
            } else {
              deviceIdToUse = null;
            }
            streamToUse = await navigator.mediaDevices.getUserMedia({
              audio: deviceIdToUse
                ? { deviceId: { exact: deviceIdToUse } }
                : true,
              video: false,
            });
            console.log(
              `Assistant: Acquired local stream. Track: ${
                streamToUse.getAudioTracks()[0]?.label
              }`
            );
          }

          microphoneStreamRef.current = streamToUse;

          const clientId = `${user.username}-${Date.now()}`;
          const callbacks = {
            onOpen: () => {
              if (!isMountedRef.current) return;
              console.log("Assistant: WebSocket opened.");
              setIsListening(true);
              setIsSpeechLoading(false);
              setSpeechError(null);
            },
            // Updated onMessage handler for WebSocket
            // Updated onMessage handler for the WebSocket
            // Update the onMessage handler in Assistant.jsx to use the existing audio playback mechanism

            // In Assistant.jsx, update the onMessage handler in the callbacks object:

            onMessage: (data) => {
              if (!isMountedRef.current) return;
              try {
                // Ensure data is a string
                const dataString =
                  typeof data === "string" ? data : JSON.stringify(data);
                const result = speechApi.parseSpeechResult(dataString);

                console.log("Assistant: Parsed WebSocket message:", result);

                // Handle recognition-type message (just the recognized text)
                if (result.type === "recognition" && result.text) {
                  console.log(
                    "Assistant: Received recognition transcript:",
                    result.text
                  );
                  setTranscript((prev) =>
                    prev ? `${prev} ${result.text}` : result.text
                  );
                }
                // Handle final messages which might include assistant response
                else if (result.type === "final") {
                  // If this message has recognized text
                  if (result.text) {
                    console.log(
                      "Assistant: Received final transcript:",
                      result.text
                    );
                    setTranscript((prev) =>
                      prev ? `${prev} ${result.text}` : result.text
                    );
                  }

                  // If we have an assistant response, process it
                  if (result.assistant_response) {
                    console.log("Assistant: Received assistant response");

                    // Process the speech text and assistant response together
                    handleAssistantResponse(
                      result.text,
                      result.assistant_response
                    );

                    // Audio playback is now handled directly in speechApiService.parseSpeechResult
                    // No need to handle it here

                    // Clear partial transcript and stop listening
                    setPartialTranscript("");
                    _stopListening();
                  }

                  // Handle session completion
                  if (result.final === true || result.status === "complete") {
                    console.log("Assistant: Session marked as complete");
                  }
                } else if (result.type === "partial") {
                  setPartialTranscript(result.text || "");
                } else if (
                  result.type === "status" &&
                  result.status === "complete"
                ) {
                  console.log(
                    "Assistant: Received 'complete' status.",
                    result.reason
                  );
                  _stopListening();
                } else if (result.type === "error") {
                  console.error(
                    "Assistant: Recognition error from server:",
                    result
                  );
                  setSpeechError(
                    `Recognition error: ${result.error || "Unknown"}`
                  );
                  _stopListening();
                }
              } catch (err) {
                console.error(
                  "Assistant: Error processing speech message:",
                  err
                );
                setSpeechError(`Processing error: ${err.message}`);
              }
            },
            onClose: (code, reason, wasNormalClose) => {
              console.log(
                `Assistant: WebSocket closed. Code: ${code}, Reason: "${reason}", Normal: ${wasNormalClose}`
              );
              if (!isMountedRef.current) return;

              if (!wasNormalClose && code !== 1000) {
                setSpeechError(
                  `Connection closed unexpectedly: ${reason || code}`
                );
              }
              setIsListening(false);
              setIsSpeechLoading(false);
              websocketRef.current = null;
              releaseMicrophoneStream();
            },
            onError: (errorMessage) => {
              console.error("Assistant: WebSocket error:", errorMessage);
              if (!isMountedRef.current) return;
              setSpeechError(errorMessage || "Speech connection failed");
              setIsListening(false);
              setIsSpeechLoading(false);
              websocketRef.current = null;
              releaseMicrophoneStream();
            },
          };

          const connection = speechApi.createSpeechConnection(
            clientId,
            callbacks,
            {
              deviceId: !isStreamSharedRef.current ? deviceIdToUse : null,
              stream: microphoneStreamRef.current,
              username: user.username, // Add the required username parameter
            }
          );

          if (connection) {
            websocketRef.current = connection;
          } else {
            throw new Error("Failed to create speech connection instance.");
          }
        } catch (error) {
          console.error(
            "Assistant: Failed to start speech recognition:",
            error
          );
          if (isMountedRef.current) {
            setSpeechError(
              error.message || "Microphone access or connection failed"
            );
            setIsListening(false);
            setIsSpeechLoading(false);
            releaseMicrophoneStream();
            websocketRef.current = null;
            isStreamSharedRef.current = false;
            microphoneStreamRef.current = null;
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      [
        audioDeviceId,
        user?.username,
        releaseMicrophoneStream,
        handleAssistantResponse,
      ]
    );

    const _stopListening = useCallback(
      (isUnmounting = false) => {
        if (!isListening && !websocketRef.current && !isSpeechLoading) {
          if (!isUnmounting) releaseMicrophoneStream();
          return;
        }
        console.log("Assistant: Stopping speech recognition...");

        if (websocketRef.current) {
          websocketRef.current.stop?.(); // Use optional chaining
          websocketRef.current = null;
        }

        if (!isUnmounting && isMountedRef.current) {
          setIsListening(false);
          setIsSpeechLoading(false);
          setSpeechError(null);
        }

        if (!isUnmounting) {
          setTimeout(() => {
            if (isMountedRef.current) releaseMicrophoneStream();
          }, 50);
        } else {
          releaseMicrophoneStream();
        }
      },
      [isListening, isSpeechLoading, releaseMicrophoneStream]
    );

    // --- UI Handlers ---

    const formatMessageForApi = (messageContent) => {
      // This function already correctly uses messageHistoryLimit for context
      const recentMessages = messages.slice(-messageHistoryLimit * 2);
      console.log("messageContent= ", messageContent); // Debugging
      console.log("recentMessages= ", recentMessages); // Debugging

      let previous_task_completed = false;
      if (recentMessages && recentMessages.length > 0) {
        const lastMessage = recentMessages[recentMessages.length - 1];
        // Ensure lastMessage and its content exist and content is a string before checking
        if (
          lastMessage &&
          lastMessage.content &&
          typeof lastMessage.content === "string"
        ) {
          previous_task_completed =
            lastMessage.content.includes("Journal Entry");
        }
      }
      console.log("previous_task_completed= ", previous_task_completed); // Debugging
      if (previous_task_completed) {
        console.log("Assistant: Previous task completed, clearing context."); // Debugging
        setMessages([]);
      }

      let formattedMessage = "";

      if (recentMessages.length > 0 && !previous_task_completed) {
        console.log("Assistant: Previous task not completed, adding context."); // Debugging
        formattedMessage += "Previous conversation:\n";
        recentMessages.forEach((msg) => {
          const role = msg.role === "user" ? "User" : "Assistant";
          formattedMessage += `${role}: ${msg.content}\n`;
        });
        formattedMessage += "\nCurrent message:\n";
      }

      formattedMessage += messageContent;

      console.log("formattedMessage= ", formattedMessage); // Debugging

      return formattedMessage;
    };

    const handleSendMessage = async () => {
      if (!inputMessage.trim() || isLoading) return;

      try {
        setIsLoading(true);
        const currentMessage = inputMessage.trim();
        const userMessage = { role: "user", content: currentMessage }; // Create message object

        // Update state, keeping only the last DISPLAY_MESSAGE_LIMIT messages
        setMessages((prev) =>
          [...prev, userMessage].slice(-DISPLAY_MESSAGE_LIMIT)
        );

        setInputMessage("");

        const messageWithContext = formatMessageForApi(currentMessage);

        const data = await assistantApi.sendMessage(
          messageWithContext,
          user.username,
          true // Enable audio generation
        );

        const assistantMessage = { role: "assistant", content: data.response }; // Create message object

        // Update state again, keeping only the last DISPLAY_MESSAGE_LIMIT messages
        setMessages((prev) =>
          [...prev, assistantMessage].slice(-DISPLAY_MESSAGE_LIMIT)
        );

        if (data.response.includes("Journal Entry for")) {
          const dateMatch = data.response.match(
            /Journal Entry for (\d{4}-\d{2}-\d{2})/
          );
          if (dateMatch && dateMatch[1]) {
            const date = new Date(dateMatch[1]);
            handleDateClick(date);
            await reloadContent(date);
          }
        }
      } catch (error) {
        console.error(error);
        const errorMessageContent = error.message
          .split("\n")
          .filter((line) => !line.trim().startsWith("at "))
          .join("\n");

        const errorMessage = {
          role: "assistant",
          content: errorMessageContent,
        }; // Create message object

        // Update state with error, keeping only the last DISPLAY_MESSAGE_LIMIT messages
        setMessages((prev) =>
          [...prev, errorMessage].slice(-DISPLAY_MESSAGE_LIMIT)
        );

        setError("Failed to send message. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleDeviceSelected = (deviceId, deviceLabel) => {
      setAudioDeviceId(deviceId);
      localStorage.setItem(STORAGE_KEY, deviceId);
      setShowDeviceSelector(false);
      console.log(
        `Assistant: Selected audio device: ${deviceLabel} (${deviceId})`
      );
      if (isListening) {
        console.log("Assistant: Restarting listening with new device.");
        _stopListening();
        setTimeout(() => _startListening(), 100);
      }
    };

    const toggleListening = () => {
      if (isListening || isSpeechLoading) {
        _stopListening();
      } else {
        _startListening(); // Call without arguments
      }
    };

    // --- JSX ---
    return (
      <Container fluid className="d-flex flex-column h-100 p-0">
        {/* Message Display Area */}
        <div className="flex-grow-1 overflow-auto p-3">
          {/* Display messages from the capped state */}
          {messages.map((message, index) => (
            <Row key={index} className="mb-3">
              <Col
                className={message.role === "user" ? "ms-auto" : ""}
                xs="auto"
                style={{ maxWidth: "80%" }}
              >
                <Card
                  className={`border-0 ${
                    message.role === "user"
                      ? "bg-primary bg-opacity-10"
                      : "bg-light"
                  }`}
                >
                  <Card.Body className="p-2">
                    <small className="fw-medium d-block mb-1">
                      {message.role === "user" ? "You" : "Assistant"}
                    </small>
                    <div className="markdown-content">
                      <ReactMarkdown
                        components={{
                          h1: ({ node, ...props }) => (
                            <h1 style={{ fontSize: "1.2rem" }} {...props} />
                          ),
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ))}
          <div ref={messagesEndRef} />

          {/* Error Displays */}
          {error && (
            <Row className="justify-content-center mb-3">
              <Col xs="auto">
                <div className="text-danger bg-danger bg-opacity-10 p-2 rounded">
                  {error}
                </div>
              </Col>
            </Row>
          )}
          {speechError && (
            <Row className="justify-content-center mb-3">
              <Col xs="auto">
                <div className="text-warning bg-warning bg-opacity-10 p-2 rounded">
                  {speechError}
                </div>
              </Col>
            </Row>
          )}
        </div>

        {/* Input Area */}
        <div className="border-top p-3 bg-light">
          <Row className="g-2 align-items-center">
            <Col>
              <Form.Control
                as="textarea"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={
                  isListening
                    ? partialTranscript
                      ? `Listening... "${partialTranscript}"`
                      : "Listening..."
                    : isSpeechLoading
                    ? "Starting microphone..."
                    : processingAssistantResponse
                    ? "Processing assistant response..."
                    : "Type your message or press Mic..."
                }
                disabled={
                  isLoading || isSpeechLoading || processingAssistantResponse
                }
                rows={1}
                style={{ resize: "none", overflowY: "auto", minHeight: "38px" }}
                className={` ${isListening ? "border-primary shadow-sm" : ""}`}
              />
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              {/* Send Button */}
              <Button
                variant="primary"
                onClick={handleSendMessage}
                disabled={
                  isLoading ||
                  isSpeechLoading ||
                  processingAssistantResponse ||
                  !inputMessage.trim()
                }
                style={{ minWidth: "70px" }}
                className="me-2"
              >
                {isLoading || processingAssistantResponse ? (
                  <Loader
                    className="spinner-border spinner-border-sm"
                    size={16}
                  />
                ) : (
                  "Send"
                )}
              </Button>

              {/* Mic Toggle Button */}
              <Button
                variant="outline-secondary"
                className={`p-0 d-flex align-items-center justify-content-center me-2 ${
                  isSpeechLoading || processingAssistantResponse
                    ? "disabled"
                    : ""
                }`}
                style={{ width: "38px", height: "38px", aspectRatio: "1 / 1" }}
                onClick={toggleListening}
                disabled={isSpeechLoading || processingAssistantResponse}
                title={
                  isListening ? "Stop listening" : "Start speech recognition"
                }
              >
                {isListening ? (
                  <MicIcon size={20} className="text-danger" />
                ) : isSpeechLoading ? (
                  <Loader
                    size={18}
                    className="text-muted spinner-border spinner-border-sm"
                  />
                ) : (
                  <MicOff size={20} className="text-muted" />
                )}
              </Button>

              {/* Settings/Device Selector Toggle */}
              <Button
                variant="outline-secondary"
                className="p-0 d-flex align-items-center justify-content-center"
                style={{ width: "38px", height: "38px", aspectRatio: "1 / 1" }}
                onClick={() => setShowDeviceSelector((s) => !s)}
                title="Select microphone"
                disabled={processingAssistantResponse}
              >
                <Settings size={18} className="text-muted" />
              </Button>
            </Col>
          </Row>
          {/* Device Selector Dropdown */}
          {showDeviceSelector && (
            <div className="mt-2">
              <AudioDeviceSelector
                onDeviceSelected={handleDeviceSelected}
                initialDeviceId={audioDeviceId}
              />
            </div>
          )}
        </div>
      </Container>
    );
  }
);
export default Assistant;
